/**
 * Standalone Notes page.
 *
 * Same backing collection (`/api/notes`) and identical fields as the notes
 * shown under Recall — editing here updates the same record visible inside
 * the meeting's Recall tab.
 *
 * Each note shows its linked meeting (title + date) when present. Clicking
 * the meeting reference opens the meeting details page.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil, Save, X as XIcon, Calendar, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatLongDateTimeInTz, userBrowserTimezone } from "@/lib/time";

export default function Notes() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const tz = user?.timezone || userBrowserTimezone();
  const [list, setList] = useState([]);
  const [content, setContent] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [meetingFilter, setMeetingFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    if (!currentWorkspace) return;
    const { data } = await api.get(`/notes`, { params: { workspace_id: currentWorkspace.workspace_id } });
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentWorkspace]);

  const add = async () => {
    if (!content.trim()) return;
    await api.post(`/notes`, { content: content.trim(), workspace_id: currentWorkspace.workspace_id });
    setContent("");
    load();
  };
  const del = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/notes/${n.note_id}`);
    load();
  };
  const startEdit = (n) => { setEditingId(n.note_id); setEditingText(n.content || ""); };
  const cancelEdit = () => { setEditingId(null); setEditingText(""); };
  const saveEdit = async () => {
    const c = editingText.trim();
    if (!c || !editingId) return;
    try {
      await api.patch(`/notes/${editingId}`, { content: c });
      toast.success("Note updated");
      cancelEdit();
      load();
    } catch { toast.error("Could not save edit"); }
  };

  // Build the meeting filter options from notes we already have so we can
  // filter without an extra round-trip.
  const meetingOptions = useMemo(() => {
    const byId = new Map();
    for (const n of list) {
      if (n.meeting_id && n.meeting_title) {
        byId.set(n.meeting_id, n.meeting_title);
      }
    }
    return Array.from(byId.entries()).map(([id, title]) => ({ id, title }));
  }, [list]);

  const filtered = useMemo(() => {
    let arr = list;
    if (meetingFilter === "__none__") arr = arr.filter((n) => !n.meeting_id);
    else if (meetingFilter) arr = arr.filter((n) => n.meeting_id === meetingFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((n) => {
        const fields = [n.content || "", n.title || "", n.meeting_title || ""];
        return fields.some((f) => String(f).toLowerCase().includes(q));
      });
    }
    // Newest first
    return [...arr].sort((a, b) => {
      const da = a.updated_at || a.created_at || "";
      const db = b.updated_at || b.created_at || "";
      return db.localeCompare(da);
    });
  }, [list, meetingFilter, search]);

  return (
    <div className="flex flex-col gap-6" data-testid="notes-page">
      <header>
        <p className="bloq-eyebrow">Notes</p>
        <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">Brain dump, organised.</h1>
        <p className="text-sm text-[#82828c] mt-3 max-w-2xl">
          Notes taken inside a meeting appear here automatically. Editing here updates the
          same record under that meeting's Recall tab.
        </p>
      </header>

      <div className="bloq-card p-4 flex flex-col gap-2">
        <textarea
          rows={3}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write a quick note (it'll appear on the Notes page)…"
          className="bloq-input"
          data-testid="note-content-input"
        />
        <button onClick={add} className="bloq-btn-primary self-end inline-flex items-center gap-2" data-testid="note-add-button">
          <Plus className="w-4 h-4" /> Save
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-[#82828c] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search notes by content or linked meeting…"
          className="bloq-input pl-9 pr-9 w-full"
          data-testid="notes-search-input"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#f5efe6]"
            data-testid="notes-search-clear"
            aria-label="Clear search"
          >
            <XIcon className="w-3.5 h-3.5 text-[#82828c]" />
          </button>
        )}
      </div>

      {meetingOptions.length > 0 && (
        <div className="flex items-center gap-2 text-sm" data-testid="notes-filter-bar">
          <Search className="w-4 h-4 text-[#82828c]" />
          <span className="text-[#82828c]">Filter by meeting:</span>
          <select
            className="bloq-input py-1.5 text-sm"
            value={meetingFilter}
            onChange={(e) => setMeetingFilter(e.target.value)}
            data-testid="notes-meeting-filter"
          >
            <option value="">All notes</option>
            <option value="__none__">Unlinked notes only</option>
            {meetingOptions.map((m) => (
              <option key={m.id} value={m.id}>{m.title}</option>
            ))}
          </select>
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-sm text-[#82828c]">No notes yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((n) => (
            <div key={n.note_id} className="bloq-card-soft p-4 group relative flex flex-col gap-2" data-testid={`note-card-${n.note_id}`}>
              {editingId === n.note_id ? (
                <>
                  <textarea
                    rows={4}
                    className="bloq-input text-sm"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    data-testid={`note-edit-input-${n.note_id}`}
                  />
                  <div className="flex justify-end gap-2">
                    <button onClick={cancelEdit} className="text-xs text-[#82828c] hover:text-[#1a1a1f] inline-flex items-center gap-1" data-testid={`note-edit-cancel-${n.note_id}`}>
                      <XIcon className="w-3 h-3" /> Cancel
                    </button>
                    <button onClick={saveEdit} className="bloq-btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1" data-testid={`note-edit-save-${n.note_id}`}>
                      <Save className="w-3 h-3" /> Save
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-[#1a1a1f] whitespace-pre-wrap break-words" data-testid={`note-text-${n.note_id}`}>{n.content}</p>
                  {n.meeting_id && (
                    n.is_meeting_host ? (
                      <Link
                        to={`/app/meetings/${n.meeting_id}#recall`}
                        className="text-[11px] text-[#005e7a] hover:underline inline-flex items-center gap-1 mt-1 w-fit"
                        data-testid={`note-meeting-link-${n.note_id}`}
                      >
                        <Calendar className="w-3 h-3" />
                        {n.meeting_title || "View meeting"}
                        {n.meeting_start && <span className="text-[#82828c]">· {formatLongDateTimeInTz(n.meeting_start, tz)}</span>}
                      </Link>
                    ) : (
                      <span
                        className="text-[11px] text-[#82828c] inline-flex items-center gap-1 mt-1 w-fit"
                        data-testid={`note-meeting-text-${n.note_id}`}
                      >
                        <Calendar className="w-3 h-3" />
                        {n.meeting_title || "From a meeting"}
                        {n.meeting_start && <span className="text-[#82828c]">· {formatLongDateTimeInTz(n.meeting_start, tz)}</span>}
                      </span>
                    )
                  )}
                  <p className="text-[10px] text-[#82828c] mt-2">
                    {new Date(n.updated_at || n.created_at).toLocaleString()}
                    {n.updated_at && n.updated_at !== n.created_at && " (edited)"}
                  </p>
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => startEdit(n)} className="p-1 rounded hover:bg-white" data-testid={`note-edit-${n.note_id}`}>
                      <Pencil className="w-3.5 h-3.5 text-[#82828c]" />
                    </button>
                    <button onClick={() => del(n)} className="p-1 rounded hover:bg-white" data-testid={`note-delete-${n.note_id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-[#82828c]" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
